import { default as React } from 'react';
import {
  useEntity,
  EntityRefLinks,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import {
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { InfoCard, MarkdownContent } from '@backstage/core-components';
import { EntityAboutCard } from '@backstage/plugin-catalog';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import EditIcon from '@material-ui/icons/Edit';
import GitHubIcon from '@material-ui/icons/GitHub';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Link as RouterLink } from 'react-router-dom';
import { useAboutCardStyles } from './aboutCardStyles';

/**
 * About card for Component entities that supports two classification models:
 *
 * - **Onspring system** (`spec.system`): delegates to the standard `EntityAboutCard`,
 *   which natively displays the "System" field.
 * - **Internal tool** (`spec.domain`): renders a custom About card that displays
 *   the owning business unit as a "Domain" field instead of "System".
 *
 * Every component has exactly one of these two fields — never both.
 */
export const ComponentAboutCard = ({ variant }: { variant?: 'gridItem' }) => {
  const { entity } = useEntity();
  const classes = useAboutCardStyles();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);

  const specDomain = (entity.spec as Record<string, unknown>)?.domain as
    | string
    | undefined;
  const specSystem = (entity.spec as Record<string, unknown>)?.system as
    | string
    | undefined;

  // Standard path: entity has spec.system → use Backstage's EntityAboutCard
  // which natively shows the System field via RELATION_PART_OF.
  if (!specDomain || specSystem) {
    return <EntityAboutCard />;
  }

  // Internal tools path: entity has spec.domain but no spec.system →
  // render a custom About card with a "Domain" field.
  const description = entity.metadata?.description || 'No description';
  const tags = entity.metadata?.tags || [];
  const specType = (entity.spec as Record<string, unknown>)?.type as
    | string
    | undefined;
  const specLifecycle = (entity.spec as Record<string, unknown>)?.lifecycle as
    | string
    | undefined;

  // Owner: read from RELATION_OWNED_BY relations (populated by catalog after ingestion)
  const ownedByRelations: string[] = (entity.relations || [])
    .filter(r => r.type === RELATION_OWNED_BY)
    .map(r => r.targetRef);

  // Fallback to spec.owner if relations not yet ingested
  const specOwner = (entity.spec as Record<string, unknown>)?.owner as
    | string
    | undefined;
  let ownerRefs: string[];
  if (ownedByRelations.length > 0) {
    ownerRefs = ownedByRelations;
  } else if (specOwner) {
    ownerRefs = [`group:default/${specOwner}`];
  } else {
    ownerRefs = [];
  }

  const namespace = entity.metadata.namespace ?? 'default';
  const domainEntityRef = `domain:${namespace}/${specDomain}`;

  // Annotations used by EntityAboutCard for edit/source/techdocs links
  const annotations = entity.metadata.annotations ?? {};
  const editUrl = annotations['backstage.io/edit-url'];
  const viewUrl = annotations['backstage.io/view-url'];
  const sourceLocation = annotations['backstage.io/source-location'];
  const techdocsRef = annotations['backstage.io/techdocs-ref'];

  // Strip the 'url:' prefix that Backstage prepends to source-location values
  const sourceUrl =
    viewUrl ||
    (sourceLocation?.startsWith('url:')
      ? sourceLocation.slice(4)
      : sourceLocation);

  const hasTechDocs = Boolean(techdocsRef);
  const techdocsPath = `/docs/${namespace}/component/${entity.metadata.name}`;

  const handleRefresh = () => {
    catalogApi.refreshEntity(stringifyEntityRef(entity)).catch(() => {
      alertApi.post({
        message: 'Failed to refresh entity.',
        severity: 'error',
      });
    });
  };

  const cardActions = (
    <>
      <Tooltip title="Refresh">
        <IconButton aria-label="Refresh" onClick={handleRefresh}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
      {editUrl && (
        <Tooltip title="Edit Metadata">
          <IconButton
            aria-label="Edit Metadata"
            component="a"
            href={editUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <InfoCard title="About" variant={variant} action={cardActions}>
      {(sourceUrl || hasTechDocs) && (
        <>
          <Box display="flex" mb={2}>
            {sourceUrl && (
              <Box mr={3}>
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.viewLink}
                >
                  <GitHubIcon fontSize="large" />
                  <Typography variant="overline" display="block">
                    View Source
                  </Typography>
                </a>
              </Box>
            )}
            {hasTechDocs && (
              <Box>
                <Link
                  component={RouterLink}
                  to={techdocsPath}
                  className={classes.viewLink}
                  underline="none"
                >
                  <DescriptionIcon fontSize="large" />
                  <Typography variant="overline" display="block">
                    View TechDocs
                  </Typography>
                </Link>
              </Box>
            )}
          </Box>
          <Divider />
          <Box mt={2} />
        </>
      )}
      <Grid container spacing={2}>
        {/* Description */}
        <Grid item xs={12}>
          <Typography variant="h2" className={classes.label}>
            Description
          </Typography>
          <div className={classes.description}>
            <MarkdownContent content={description} />
          </div>
        </Grid>

        {/* Owner */}
        {ownerRefs.length > 0 && (
          <Grid item xs={12} sm={6} lg={4}>
            <Typography variant="h2" className={classes.label}>
              Owner
            </Typography>
            <Typography variant="body2" className={classes.value}>
              <EntityRefLinks entityRefs={ownerRefs} defaultKind="group" />
            </Typography>
          </Grid>
        )}

        {/* Domain — shown in place of System for internal tools */}
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="h2" className={classes.label}>
            Domain
          </Typography>
          <Typography variant="body2" className={classes.value}>
            <EntityRefLinks
              entityRefs={[domainEntityRef]}
              defaultKind="domain"
            />
          </Typography>
        </Grid>

        {/* Type */}
        {specType && (
          <Grid item xs={12} sm={6} lg={4}>
            <Typography variant="h2" className={classes.label}>
              Type
            </Typography>
            <Typography variant="body2" className={classes.value}>
              {specType}
            </Typography>
          </Grid>
        )}

        {/* Lifecycle */}
        {specLifecycle && (
          <Grid item xs={12} sm={6} lg={4}>
            <Typography variant="h2" className={classes.label}>
              Lifecycle
            </Typography>
            <Typography variant="body2" className={classes.value}>
              {specLifecycle}
            </Typography>
          </Grid>
        )}

        {/* Tags */}
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="h2" className={classes.label}>
            Tags
          </Typography>
          {tags.length > 0 ? (
            tags.map(tag => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                className={classes.chip}
              />
            ))
          ) : (
            <Typography variant="body2" className={classes.value}>
              No Tags
            </Typography>
          )}
        </Grid>
      </Grid>
    </InfoCard>
  );
};
